import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    Link,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    CreateOrderParams,
    GetOrdersParams,
    createOrder,
    getOrders,
    getOrderStatuses,
} from '../../orders/orders.api';
import { Order } from '../../orders/orders.types';
import OrderDialog from '../../orders/components/OrderDialog';
import Fp1OrderCreateDialog from '../components/Fp1OrderCreateDialog';
import ShippingEvidenceDialog from '../../orders/components/ShippingEvidenceDialog';
import { OrderColumnKey, classifyOrderStatus } from '../utils/orderStatusColumn';

// ─────────────────────────────────────────────────────────────────────────────
// Tunables — kept named + near the top so they can be adjusted without hunting.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How long an order may sit in a non-completed column (measured in *business*
 * days since its last status change / creation) before it is flagged as
 * "stuck". This is the core signal of this board: surfacing orders whose pod is
 * likely already delivered while the 1TUL order — and therefore the athlete's
 * app gate — is still blocked. Tune here.
 */
const STUCK_THRESHOLD_BUSINESS_DAYS = 5;

/** Per-status page size for in-flight columns. In-flight volume is bounded, so
 *  we pull essentially all of it to make sure OLD stuck orders surface too. */
const IN_FLIGHT_PER_STATUS = 300;

/** Completed history is unbounded, so cap how many recent completed orders we
 *  pull. This column is only for "how recently are orders finishing?". */
const COMPLETED_LIMIT = 60;

/** Known flow statuses we always fetch even if the statuses endpoint is flaky. */
const CORE_FLOW_STATUSES = ['received', 'processing', 'shipping', 'completed'];

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The list endpoint's typed `Order` only carries `created_at`. Some deployments
 * return richer timestamps; we read them opportunistically (falling back to
 * `created_at`) without depending on a backend change.
 */
type BoardOrder = Order & {
    updated_at?: string | Date | null;
    completed_at?: string | Date | null;
    delivered_at?: string | Date | null;
    status_changed_at?: string | Date | null;
};

interface ColumnDef {
    key: OrderColumnKey;
    title: string;
}

const COLUMNS: ColumnDef[] = [
    { key: 'processing', title: 'Processing' },
    { key: 'in_transit', title: 'In Transit / Shipping' },
    { key: 'completed', title: 'Completed' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────────────────────

const toDate = (value: string | Date | null | undefined): Date | null => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const createdDate = (o: BoardOrder): Date | null => toDate(o.created_at);

/** Most meaningful "last active" timestamp — used for sorting and stuck age. */
const activityDate = (o: BoardOrder): Date | null =>
    toDate(o.status_changed_at) ?? toDate(o.updated_at) ?? toDate(o.created_at);

/** Best available completion timestamp; null if the payload doesn't carry one. */
const completionDate = (o: BoardOrder): Date | null =>
    toDate(o.delivered_at) ?? toDate(o.completed_at) ?? toDate(o.updated_at);

/** Count weekdays (Mon–Fri) elapsed between two dates. */
const businessDaysBetween = (from: Date, to: Date): number => {
    if (to <= from) return 0;
    let count = 0;
    const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (cursor < end) {
        cursor.setDate(cursor.getDate() + 1);
        const day = cursor.getDay();
        if (day !== 0 && day !== 6) count += 1;
    }
    return count;
};

const formatDate = (d: Date | null): string =>
    d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const buyerName = (o: BoardOrder): string => {
    if (o.buyer_org_name) return o.buyer_org_name;
    const first = o.buyer_athlete?.first_name ?? '';
    const last = o.buyer_athlete?.last_name ?? '';
    const name = `${first} ${last}`.trim();
    return name || 'Unknown buyer';
};

const shortId = (uuid: string): string => uuid.slice(0, 8);

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const Fp1OrdersBoard: React.FC = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

    const [orders, setOrders] = useState<BoardOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    // "now" reference for stuck-age math; refreshed each time the board reloads.
    const [now, setNow] = useState<Date>(() => new Date());

    const [selectedOrder, setSelectedOrder] = useState<BoardOrder | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [evidenceOrder, setEvidenceOrder] = useState<BoardOrder | null>(null);
    const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');

    const fetchBoard = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Build the set of statuses to fetch: the known core flow statuses
            // plus any live statuses that classify into an active flow column.
            // Inactive statuses (cancelled/backfilled/archived) are intentionally
            // excluded from the board — they remain visible in the Table view.
            let statusList: string[] = [...CORE_FLOW_STATUSES];
            try {
                const statusResp = await getOrderStatuses();
                const raw = statusResp.data;
                const live: string[] = Array.isArray(raw)
                    ? raw
                          .map((item: unknown) =>
                              typeof item === 'string'
                                  ? item
                                  : item &&
                                    typeof item === 'object' &&
                                    'value' in item &&
                                    typeof (item as { value: unknown }).value === 'string'
                                  ? (item as { value: string }).value
                                  : '',
                          )
                          .filter(Boolean)
                    : [];
                statusList = Array.from(new Set([...statusList, ...live]));
            } catch {
                // Fall back to the core flow statuses only.
            }

            const fetchable = statusList.filter((s) => !classifyOrderStatus(s).inactive);

            const responses = await Promise.all(
                fetchable.map((status) => {
                    const column = classifyOrderStatus(status).column;
                    const perPage = column === 'completed' ? COMPLETED_LIMIT : IN_FLIGHT_PER_STATUS;
                    return getOrders({
                        order_status: status as GetOrdersParams['order_status'],
                        sort: '-created_at',
                        per_page: perPage,
                    }).catch(() => null);
                }),
            );

            const merged = new Map<string, BoardOrder>();
            responses.forEach((resp) => {
                const rows: BoardOrder[] = resp?.data?.data ?? [];
                rows.forEach((row) => {
                    if (row?.uuid && !merged.has(row.uuid)) merged.set(row.uuid, row);
                });
            });

            setOrders(Array.from(merged.values()));
            setNow(new Date());
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to load orders');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBoard();
    }, [fetchBoard]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return orders;
        return orders.filter(
            (o) => buyerName(o).toLowerCase().includes(q) || o.uuid.toLowerCase().includes(q),
        );
    }, [orders, search]);

    const columns = useMemo(() => {
        const buckets: Record<OrderColumnKey, BoardOrder[]> = {
            processing: [],
            in_transit: [],
            completed: [],
        };
        filtered.forEach((o) => {
            buckets[classifyOrderStatus(o.order_status).column].push(o);
        });
        // Sort each column by most-recently-active first.
        (Object.keys(buckets) as OrderColumnKey[]).forEach((key) => {
            buckets[key].sort((a, b) => {
                const da = activityDate(a)?.getTime() ?? 0;
                const db = activityDate(b)?.getTime() ?? 0;
                return db - da;
            });
        });
        return buckets;
    }, [filtered]);

    const openEdit = (order: BoardOrder) => {
        setSelectedOrder(order);
        setEditOpen(true);
    };

    const handleSaved = async () => {
        setEditOpen(false);
        await fetchBoard();
    };

    // Same create flow as the Table view — the board is the default landing view, so an
    // admin should never have to switch views just to start an order.
    const handleCreateOrder = async (orderData: CreateOrderParams) => {
        try {
            await createOrder(orderData);
            setCreateOrderDialogOpen(false);
            await fetchBoard();
            setSnackMessage('Order created successfully');
        } catch (e: any) {
            console.error('Failed to create order:', e);
            setSnackMessage(e?.response?.data?.message || 'Failed to create order');
        }
    };

    const renderCard = (order: BoardOrder, columnKey: OrderColumnKey) => {
        const classification = classifyOrderStatus(order.order_status);
        const isCompletedColumn = columnKey === 'completed';

        // Stuck detection: only for active (processing / in-transit) cards.
        const reference = activityDate(order) ?? createdDate(order);
        const businessDaysStuck =
            !isCompletedColumn && !classification.inactive && reference
                ? businessDaysBetween(reference, now)
                : 0;
        const isStuck = businessDaysStuck > STUCK_THRESHOLD_BUSINESS_DAYS;

        const accentColor = isStuck
            ? theme.palette.error.main
            : classification.unrecognized
            ? theme.palette.warning.main
            : 'transparent';

        const dateForCard = isCompletedColumn
            ? completionDate(order) ?? createdDate(order)
            : createdDate(order);
        const dateLabel = isCompletedColumn ? 'Completed' : 'Ordered';

        return (
            <Paper
                key={order.uuid}
                variant="outlined"
                sx={{
                    p: 1.25,
                    mb: 1,
                    borderRadius: 2,
                    borderLeft: `4px solid ${accentColor}`,
                    opacity: classification.inactive ? 0.7 : 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Tooltip title="Open order details / edit">
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => openEdit(order)}
                            sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}
                        >
                            #{shortId(order.uuid)}
                        </Link>
                    </Tooltip>
                    <Chip
                        size="small"
                        label={order.order_status || 'unknown'}
                        color={
                            classification.unrecognized
                                ? 'warning'
                                : classification.inactive
                                ? 'default'
                                : isCompletedColumn
                                ? 'success'
                                : 'primary'
                        }
                        variant={classification.inactive ? 'outlined' : 'filled'}
                        sx={{ textTransform: 'capitalize' }}
                    />
                </Box>

                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }} noWrap>
                    {buyerName(order)}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {dateLabel}: {formatDate(dateForCard)}
                    </Typography>
                    {order.order_type ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {order.order_type.replace(/_/g, ' ')}
                        </Typography>
                    ) : null}
                </Box>

                {(isStuck || classification.unrecognized) && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                        {isStuck && (
                            <Chip
                                size="small"
                                color="error"
                                icon={<WarningAmberIcon />}
                                label={`Stuck ${businessDaysStuck} business days`}
                            />
                        )}
                        {classification.unrecognized && (
                            <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                icon={<HelpOutlineIcon />}
                                label="Unrecognized status"
                            />
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button size="small" variant="contained" onClick={() => openEdit(order)}>
                        Edit
                    </Button>
                    {order.shipping_tracking_number && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setEvidenceOrder(order)}
                        >
                            Evidence
                        </Button>
                    )}
                </Box>
            </Paper>
        );
    };

    return (
        <Box sx={{ px: 2, pb: 4 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <TextField
                    size="small"
                    placeholder="Filter by athlete / buyer name or order id"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 320, flexGrow: 1, maxWidth: 480 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loading && <CircularProgress size={20} />}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchBoard}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOrderDialogOpen(true)}
                    >
                        Create Order
                    </Button>
                </Box>
            </Box>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Box
                sx={{
                    display: isNarrow ? 'block' : 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(280px, 1fr))',
                    gap: 2,
                }}
            >
                {COLUMNS.map((col) => {
                    const items = columns[col.key];
                    const stuckCount = items.filter((o) => {
                        if (col.key === 'completed') return false;
                        const c = classifyOrderStatus(o.order_status);
                        if (c.inactive) return false;
                        const ref = activityDate(o) ?? createdDate(o);
                        return (
                            !!ref && businessDaysBetween(ref, now) > STUCK_THRESHOLD_BUSINESS_DAYS
                        );
                    }).length;

                    return (
                        <Paper
                            key={col.key}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                p: 1.5,
                                mb: isNarrow ? 2 : 0,
                                bgcolor: theme.palette.action.hover,
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: isNarrow ? 'none' : '70vh',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {col.title}
                                    </Typography>
                                    <Chip size="small" label={items.length} />
                                </Box>
                                {stuckCount > 0 && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        icon={<WarningAmberIcon />}
                                        label={`${stuckCount} stuck`}
                                    />
                                )}
                            </Box>

                            <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 0.5 }}>
                                {items.length === 0 && !loading ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ py: 2, textAlign: 'center' }}
                                    >
                                        No orders
                                    </Typography>
                                ) : (
                                    items.map((order) => renderCard(order, col.key))
                                )}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            <OrderDialog
                open={editOpen}
                order={selectedOrder}
                onClose={() => setEditOpen(false)}
                onSave={handleSaved}
                fp1HardwareOrdersTab
                onFp1OrderRefresh={(o) => setSelectedOrder(o as BoardOrder)}
            />
            <ShippingEvidenceDialog
                open={evidenceOrder !== null}
                order={evidenceOrder}
                onClose={() => setEvidenceOrder(null)}
            />
            <Fp1OrderCreateDialog
                open={createOrderDialogOpen}
                onClose={() => setCreateOrderDialogOpen(false)}
                onSubmit={handleCreateOrder}
            />
            <Snackbar
                open={!!snackMessage}
                autoHideDuration={6000}
                onClose={() => setSnackMessage('')}
                message={snackMessage}
            />
        </Box>
    );
};

export default Fp1OrdersBoard;
